import React from 'react'
import { Typography, Grid } from '@the-platform-group/component-library'
import { Button } from '@the-platform-group/component-library/Button'
import { getFullName } from '@the-platform-group/formatters/people'
import { usePeopleMergeContext } from 'pages/MergeProfiles/contexts/PeopleMergeContext'
import { useConfirmationDialog } from 'components/ConfirmationDialog'
import {
  getJobTitleAndOrgName,
  getJobFieldValue,
  getJobOverrideValue,
} from 'pages/MergeProfiles/utils'
import { JOB_DETAILS_DRAWER_FIELDS } from 'pages/MergeProfiles/constants'
import JobDetailsTable from './JobDetailsTable'
import BaseJobDrawer from './MultipleJobsSection/BaseJobDrawer'

/**
 * JobPreviewDrawer - Unified drawer for viewing mapped job details
 *
 * Used in two contexts:
 * 1. Multiple Jobs step - with Edit/Delete actions (pass onEdit prop)
 * 2. Preview & Merge step - read-only view (no onEdit prop)
 *
 * @param {Object} props
 * @param {boolean} props.open - Controls drawer visibility
 * @param {Function} props.onClose - Callback to close the drawer
 * @param {string} props.selectedTargetJobId - The FactSet job ID to display
 * @param {Function} [props.onEdit] - Optional callback to edit mapping (shows action buttons when provided)
 *
 * @returns {React.Component|null} The job preview drawer or null if no job is selected
 */
const JobPreviewDrawer = ({ open, onClose, selectedTargetJobId, onEdit }) => {
  const { mergeData, jobMappingData } = usePeopleMergeContext()
  const { sourceJobs, targetJobs, targetPerson, preview } = mergeData
  const { getMappedSourceJobsForTarget, removeMappingsForTarget } = jobMappingData
  const { createConfirmationDialog } = useConfirmationDialog()

  const selectedTargetJob = selectedTargetJobId
    ? targetJobs.find(job => job.id === selectedTargetJobId)
    : null

  const mappedSourceJobs = getMappedSourceJobsForTarget(selectedTargetJobId, sourceJobs)

  const handleEdit = () => {
    if (mappedSourceJobs.length > 0 && selectedTargetJobId && onEdit) {
      const sourceJobIds = mappedSourceJobs.map(job => job.id)
      onEdit(sourceJobIds, selectedTargetJobId)
    }
  }

  const handleDelete = () => {
    createConfirmationDialog({
      title: 'Remove Job',
      body: 'Are you sure you want to remove this mapped job?',
      confirmButtonText: 'Remove',
      onConfirm: () => {
        removeMappingsForTarget(selectedTargetJobId)
        onClose()
      },
    })
  }

  if (!selectedTargetJob) {
    return null
  }

  const footer = (
    <Grid container justifyContent={onEdit ? 'space-between' : 'flex-end'}>
      {onEdit && (
        <Grid item xs={6}>
          <Button variant="outlined" color="error" onClick={handleDelete}>
            Remove Job
          </Button>
        </Grid>
      )}
      <Grid item xs={6} container justifyContent="flex-end" spacing={1}>
        <Grid item>
          <Button variant="text" onClick={onClose}>
            Close
          </Button>
        </Grid>
        {onEdit && (
          <Grid item>
            <Button variant="contained" onClick={handleEdit}>
              Edit
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  )

  return (
    <BaseJobDrawer open={open} onClose={onClose} title="Mapped Job Preview Details" footer={footer}>
      <Grid container direction="column" spacing={3}>
        <Grid item>
          <Typography variant="body1" fontWeight="bold">
            {getFullName(targetPerson)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            FactSet Job: {getJobTitleAndOrgName(selectedTargetJob)}
          </Typography>
        </Grid>

        {mappedSourceJobs.length > 0 && (
          <Grid item>
            <Typography variant="body2" color="text.secondary">
              Mapped Platform Jobs:
            </Typography>
            {mappedSourceJobs.map(job => (
              <Typography key={job.id} variant="body2" sx={{ ml: 2 }}>
                {getJobTitleAndOrgName(job)}
              </Typography>
            ))}
          </Grid>
        )}

        <Grid item>
          <JobDetailsTable
            fields={JOB_DETAILS_DRAWER_FIELDS}
            selectedTargetJob={selectedTargetJob}
            getFieldValue={(job, fieldKey) =>
              onEdit
                ? getJobFieldValue(job, fieldKey, { preview, isSource: false })
                : getJobFieldValue(job, fieldKey)
            }
            getOverrideValue={fieldKey => getJobOverrideValue(mappedSourceJobs, fieldKey)}
          />
        </Grid>
      </Grid>
    </BaseJobDrawer>
  )
}

export default JobPreviewDrawer
